import { User, UserOrganization } from '@/core/domain/user-model';

const LAST_ORG_ID_KEY = 'last_org';
const LAST_ORG_SEGMENT_KEY = 'last_org_slug';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type OrganizationRef = { id: string; slug?: string | null };

/** Segmento que representa a organizacao na URL. Prefere o slug e cai para o id. */
export function organizationRouteSegment(organization?: OrganizationRef | null): string | null {
  if (!organization) return null;
  const slug = organization.slug?.trim();
  if (slug) return slug;
  const id = organization.id?.trim();
  return id ? id : null;
}

/** Um segmento de URL antigo, anterior ao slug, e sempre um UUID. */
export function isOrganizationId(ref?: string | null): boolean {
  return !!ref && UUID_PATTERN.test(ref.trim());
}

export function matchesOrganizationRef(
  organization: OrganizationRef | null | undefined,
  ref: string | null | undefined,
): boolean {
  if (!organization || !ref) return false;
  const normalized = ref.trim().toLowerCase();
  if (!normalized) return false;
  return (
    organization.id?.trim().toLowerCase() === normalized ||
    (organization.slug ?? '').trim().toLowerCase() === normalized
  );
}

/** Resolve um segmento de URL (slug ou id) para uma organizacao do usuario. */
export function resolveOrganizationRef(
  ref: string | null | undefined,
  user: User | null | undefined,
): UserOrganization | null {
  if (!ref || !user) return null;
  if (matchesOrganizationRef(user.current_organization, ref)) {
    return user.current_organization ?? null;
  }
  return (
    user.organizations?.find((organization) => matchesOrganizationRef(organization, ref)) ?? null
  );
}

/** Id usado nas chamadas de API a partir de um segmento de URL que pode ser slug ou id. */
export function organizationIdFromRef(
  ref: string | null | undefined,
  user: User | null | undefined,
): string | null {
  const organization = resolveOrganizationRef(ref, user);
  if (organization) return organization.id;
  const normalized = ref?.trim();
  return isOrganizationId(normalized) ? (normalized ?? null) : null;
}

/** Organizacao ativa do usuario. O login as vezes traz so o current_organization_id. */
export function currentOrganizationOf(user: User | null | undefined): OrganizationRef | null {
  if (!user) return null;
  if (user.current_organization) return user.current_organization;
  const id = user.current_organization_id;
  if (!id) return null;
  return user.organizations?.find((organization) => organization.id === id) ?? { id };
}

export function dashboardUrl(segment: string): string {
  return `/${segment}/dashboard`;
}

export function storeLastOrganization(organization?: OrganizationRef | null): void {
  if (!organization) return;
  if (organization.id) localStorage.setItem(LAST_ORG_ID_KEY, organization.id);
  const segment = organizationRouteSegment(organization);
  if (segment) localStorage.setItem(LAST_ORG_SEGMENT_KEY, segment);
}

export function clearLastOrganization(): void {
  localStorage.removeItem(LAST_ORG_ID_KEY);
  localStorage.removeItem(LAST_ORG_SEGMENT_KEY);
}

/** Segmento da ultima organizacao. Cai para o id enquanto o slug nao foi gravado. */
export function readLastOrganizationSegment(): string | null {
  const segment =
    localStorage.getItem(LAST_ORG_SEGMENT_KEY) || localStorage.getItem(LAST_ORG_ID_KEY);
  return segment && segment.trim().length > 0 ? segment.trim() : null;
}

export function readLastOrganizationId(): string | null {
  const id = localStorage.getItem(LAST_ORG_ID_KEY);
  return id && id.trim().length > 0 ? id.trim() : null;
}
